<template>
  <div class="page-container reveal sms-page">
    <div class="sms-hero">
      <div>
        <div class="sms-eyebrow">Communication Opérationnelle</div>
        <h2>SMS Center</h2>
        <p>Configure les SMS automatiques, envoie des annonces manuelles, et surveille l’historique d’envoi.</p>
      </div>
      <div class="sms-hero-stats">
        <div class="hero-stat">
          <strong>{{ logs.length }}</strong>
          <span>logs chargés</span>
        </div>
        <div class="hero-stat">
          <strong>{{ sentCount }}</strong>
          <span>envoyés</span>
        </div>
        <div class="hero-stat">
          <strong>{{ failedCount }}</strong>
          <span>échecs</span>
        </div>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="sms-tabs">
      <el-tab-pane v-if="canManagePolicy" label="Politique" name="policy">
        <div class="sms-card">
          <div class="card-head">
            <div>
              <h3>Règles automatiques</h3>
              <p>Active ou coupe les SMS non critiques par type d’événement.</p>
            </div>
            <el-button type="primary" :loading="savingPolicy" @click="savePolicy">Enregistrer</el-button>
          </div>

          <div class="policy-grid">
            <div class="policy-item policy-master">
              <div>
                <strong>SMS non critiques globaux</strong>
                <p>Coupe l’ensemble des fallback SMS non critiques.</p>
              </div>
              <el-switch v-model="policy.non_critical_global_enabled" />
            </div>

            <div v-for="item in policyFields" :key="item.key" class="policy-item">
              <div>
                <strong>{{ item.label }}</strong>
                <p>{{ item.description }}</p>
              </div>
              <el-switch v-model="policy[item.key]" />
            </div>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane v-if="canBroadcast" label="Envoi Manuel" name="broadcast">
        <div class="sms-card">
          <div class="card-head">
            <div>
              <h3>Envoyer un SMS manuel</h3>
              <p>Utile pour incident, maintenance, alerte service ou annonce ponctuelle.</p>
            </div>
          </div>

          <div class="broadcast-grid">
            <el-form label-position="top">
              <el-form-item label="Audience">
                <el-select v-model="broadcast.target_type" style="width: 100%">
                  <el-option label="Tous les utilisateurs" value="all" />
                  <el-option label="Clients" value="client" />
                  <el-option label="Supports" value="support" />
                  <el-option label="Admins" value="admin" />
                  <el-option label="Numéro spécifique" value="specific_number" />
                  <el-option label="Liste de numéros" value="specific_list" />
                </el-select>
              </el-form-item>

              <el-form-item v-if="broadcast.target_type === 'specific_number'" label="Numéro">
                <el-input v-model="broadcast.phone" placeholder="Ex: 70000000" />
              </el-form-item>

              <el-form-item v-if="broadcast.target_type === 'specific_list'" label="Liste de numéros">
                <el-input
                  v-model="broadcast.phoneListText"
                  type="textarea"
                  :rows="5"
                  placeholder="Un numéro par ligne ou séparés par virgule"
                />
              </el-form-item>

              <el-form-item label="Message">
                <el-input
                  v-model="broadcast.message"
                  type="textarea"
                  :rows="6"
                  maxlength="500"
                  show-word-limit
                  placeholder="Décris l'incident, la maintenance ou l'annonce."
                />
              </el-form-item>

              <el-checkbox v-model="broadcast.respect_opt_out">
                Respecter la préférence “pas de SMS non critiques”
              </el-checkbox>

              <div class="broadcast-actions">
                <el-button type="primary" :loading="sendingBroadcast" @click="submitBroadcast">
                  Envoyer le SMS
                </el-button>
              </div>
            </el-form>

            <div class="broadcast-preview">
              <h4>Aperçu</h4>
              <div class="preview-bubble">
                <strong>ProFissa:</strong>
                <span>{{ previewText }}</span>
              </div>
              <div v-if="lastBroadcast" class="broadcast-result">
                <h4>Dernier envoi</h4>
                <p><strong>ID:</strong> {{ lastBroadcast.broadcast_id }}</p>
                <p><strong>Demandés:</strong> {{ lastBroadcast.requested }}</p>
                <p><strong>Envoyés:</strong> {{ lastBroadcast.sent }}</p>
                <p><strong>Ignorés:</strong> {{ lastBroadcast.skipped }}</p>
                <p><strong>Échecs:</strong> {{ lastBroadcast.failed }}</p>
              </div>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane v-if="canReadLogs" label="Logs" name="logs">
        <div class="sms-card">
          <div class="card-head">
            <div>
              <h3>Historique SMS</h3>
              <p>Trace des envois, skips et erreurs.</p>
            </div>
            <div class="log-filters">
              <el-input v-model="filters.event" placeholder="Event exact" clearable style="width: 180px" />
              <el-select v-model="filters.status" clearable placeholder="Statut" style="width: 180px">
                <el-option label="Tous" value="" />
                <el-option label="Envoyé" value="sent" />
                <el-option label="Échec" value="failed" />
                <el-option label="Ignoré (Politique)" value="skipped_policy_disabled" />
                <el-option label="Ignoré (Push dispo)" value="skipped_push_available" />
                <el-option label="Ignoré (Opt-out)" value="skipped_opt_out" />
                <el-option label="Doublon" value="duplicate" />
              </el-select>
              <el-date-picker
                v-model="filters.dateRange"
                type="daterange"
                range-separator="À"
                start-placeholder="Début"
                end-placeholder="Fin"
                value-format="YYYY-MM-DD"
                style="width: 240px"
              />
              <el-button @click="loadLogs" :loading="loadingLogs">Rafraîchir</el-button>
            </div>
          </div>

          <el-table :data="logs" stripe height="520">
            <el-table-column label="Date" width="160">
              <template #default="{ row }">{{ fmtDate(row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="Événement" min-width="180">
              <template #default="{ row }">{{ formatEvent(row.event) }}</template>
            </el-table-column>
            <el-table-column prop="phone" label="Numéro" width="140" />
            <el-table-column label="Statut" width="180">
              <template #default="{ row }">
                <el-tag :type="row.status === 'sent' ? 'success' : row.status === 'failed' ? 'danger' : 'info'" size="small">
                  {{ formatStatus(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="message" label="Message" min-width="280" show-overflow-tooltip />
            <el-table-column prop="error_detail" label="Erreur" min-width="220" show-overflow-tooltip />
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElNotification } from 'element-plus'
import { extractApiError } from '../api'
import { getSmsPolicy, listSmsLogs, sendSmsBroadcast, updateSmsPolicy } from '../services/adminApi'
import { useAdminPermissions } from '../composables/useAdminPermissions'

const { hasPermission } = useAdminPermissions()
const canManagePolicy = computed(() => hasPermission('sms.policy.manage'))
const canBroadcast = computed(() => hasPermission('sms.broadcast.send'))
const canReadLogs = computed(() => hasPermission('sms.logs.read'))

const activeTab = ref(canManagePolicy.value ? 'policy' : (canBroadcast.value ? 'broadcast' : 'logs'))
const savingPolicy = ref(false)
const sendingBroadcast = ref(false)
const loadingLogs = ref(false)

const policy = ref({
  non_critical_global_enabled: true,
  mission_assigned_enabled: true,
  mission_quote_ready_enabled: true,
  mission_payment_confirmed_enabled: true,
  mission_accepted_enabled: true,
  mission_en_route_enabled: true,
  mission_arrived_enabled: true,
  mission_completed_enabled: true,
  dispute_opened_enabled: true,
  sav_opened_enabled: true,
  org_member_invited_enabled: true,
  scheduled_activation_enabled: true,
  artisan_phone_verification_warning_enabled: true,
})

const policyFields = [
  { key: 'mission_assigned_enabled', label: 'Mission assignée', description: 'Fallback SMS pour mission réservée ou dispatchée.' },
  { key: 'mission_quote_ready_enabled', label: 'Devis prêt', description: 'SMS quand le devis est disponible.' },
  { key: 'mission_payment_confirmed_enabled', label: 'Paiement confirmé', description: 'SMS quand le paiement est validé.' },
  { key: 'mission_accepted_enabled', label: 'Mission acceptée', description: 'SMS quand l’artisan accepte.' },
  { key: 'mission_en_route_enabled', label: 'Artisan en route', description: 'SMS de progression avant arrivée.' },
  { key: 'mission_arrived_enabled', label: 'Artisan arrivé', description: 'SMS à l’arrivée sur site.' },
  { key: 'mission_completed_enabled', label: 'Mission terminée', description: 'SMS de fin de mission.' },
  { key: 'dispute_opened_enabled', label: 'Litige ouvert', description: 'SMS si un litige est ouvert.' },
  { key: 'sav_opened_enabled', label: 'SAV ouvert', description: 'SMS si une demande SAV est ouverte.' },
  { key: 'org_member_invited_enabled', label: 'Invitation organisation', description: 'SMS quand un membre rejoint une organisation.' },
  { key: 'scheduled_activation_enabled', label: 'Activation planifiée', description: 'SMS quand une demande planifiée démarre.' },
  { key: 'artisan_phone_verification_warning_enabled', label: 'Avertissement vérification téléphone', description: 'SMS avant suppression d’un artisan non vérifié.' },
]

const broadcast = ref({
  target_type: 'all',
  phone: '',
  phoneListText: '',
  message: '',
  respect_opt_out: false,
})

const lastBroadcast = ref(null)
const logs = ref([])
const filters = ref({ event: '', status: '', dateRange: [] })

const previewText = computed(() => broadcast.value.message?.trim() || 'Votre message apparaîtra ici.')
const sentCount = computed(() => logs.value.filter((row) => row.status === 'sent').length)
const failedCount = computed(() => logs.value.filter((row) => row.status === 'failed').length)

const statusLabels = {
  sent: 'Envoyé',
  failed: 'Échec',
  skipped_policy_disabled: 'Ignoré (Politique)',
  skipped_push_available: 'Ignoré (Push dispo)',
  skipped_opt_out: 'Ignoré (Opt-out)',
  duplicate: 'Doublon'
}
const formatStatus = (s) => statusLabels[s] || s
const formatEvent = (e) => {
  if (!e) return '-'
  if (e === 'broadcast_manual') return 'Annonce manuelle'
  const field = policyFields.find(f => f.key === e + '_enabled' || f.key === e)
  return field ? field.label : e
}
const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const notify = (type, title, message) => ElNotification({ type, title, message, duration: 4000 })

async function loadPolicy() {
  if (!canManagePolicy.value && !canReadLogs.value) return
  try {
    policy.value = await getSmsPolicy()
  } catch (error) {
    notify('error', 'SMS Policy', extractApiError(error))
  }
}

async function savePolicy() {
  savingPolicy.value = true
  try {
    policy.value = await updateSmsPolicy(policy.value)
    notify('success', 'SMS Policy', 'Politique SMS enregistrée.')
  } catch (error) {
    notify('error', 'SMS Policy', extractApiError(error))
  } finally {
    savingPolicy.value = false
  }
}

async function submitBroadcast() {
  sendingBroadcast.value = true
  try {
    const phones = broadcast.value.target_type === 'specific_list'
      ? broadcast.value.phoneListText.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean)
      : undefined

    lastBroadcast.value = await sendSmsBroadcast({
      target_type: broadcast.value.target_type,
      phone: broadcast.value.phone || undefined,
      phones,
      message: broadcast.value.message,
      respect_opt_out: broadcast.value.respect_opt_out,
    })
    notify('success', 'Broadcast SMS', `${lastBroadcast.value.sent} SMS envoyés.`)
    await loadLogs()
  } catch (error) {
    notify('error', 'Broadcast SMS', extractApiError(error))
  } finally {
    sendingBroadcast.value = false
  }
}

async function loadLogs() {
  if (!canReadLogs.value) return
  loadingLogs.value = true
  try {
    logs.value = await listSmsLogs({
      limit: 120,
      event: filters.value.event || undefined,
      status: filters.value.status || undefined,
      start_date: filters.value.dateRange?.[0] ? `${filters.value.dateRange[0]}T00:00:00Z` : undefined,
      end_date: filters.value.dateRange?.[1] ? `${filters.value.dateRange[1]}T23:59:59Z` : undefined,
    })
  } catch (error) {
    notify('error', 'SMS Logs', extractApiError(error))
  } finally {
    loadingLogs.value = false
  }
}

onMounted(async () => {
  if (canManagePolicy.value) await loadPolicy()
  if (canReadLogs.value) await loadLogs()
})
</script>

<style scoped>
.sms-page { display: flex; flex-direction: column; gap: 1.5rem; }
.sms-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(9, 36, 70, 0.95), rgba(7, 78, 96, 0.9));
  border: 1px solid rgba(255,255,255,0.08);
}
.sms-hero h2 { margin: 0 0 0.35rem; color: #fff; font-size: 1.9rem; }
.sms-hero p { margin: 0; color: rgba(255,255,255,0.75); max-width: 58ch; }
.sms-eyebrow { text-transform: uppercase; letter-spacing: 0.14em; color: #7dd3fc; font-size: 0.75rem; margin-bottom: 0.5rem; }
.sms-hero-stats { display: flex; gap: 0.85rem; align-items: stretch; }
.hero-stat {
  min-width: 110px;
  padding: 1rem;
  border-radius: 18px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.hero-stat strong { color: #fff; font-size: 1.5rem; }
.hero-stat span { color: rgba(255,255,255,0.72); font-size: 0.82rem; }
.sms-card {
  padding: 1.25rem;
  border-radius: 22px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
}
.card-head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}
.card-head h3 { margin: 0; }
.card-head p { margin: 0.35rem 0 0; color: var(--text-muted); }
.policy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.9rem;
}
.policy-item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border-radius: 16px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
}
.policy-item strong { display: block; margin-bottom: 0.25rem; }
.policy-item p { margin: 0; color: var(--text-muted); font-size: 0.86rem; }
.policy-master {
  grid-column: 1 / -1;
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.2);
}
.broadcast-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.75fr);
  gap: 1rem;
}
.broadcast-preview {
  padding: 1rem;
  border-radius: 18px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
}
.preview-bubble {
  margin-top: 0.75rem;
  padding: 1rem;
  border-radius: 16px 16px 16px 6px;
  background: rgba(14, 116, 144, 0.18);
  border: 1px solid rgba(125, 211, 252, 0.2);
  display: flex;
  gap: 0.4rem;
  color: var(--text-main);
}
.broadcast-result {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255,255,255,0.08);
}
.broadcast-actions { margin-top: 1rem; display: flex; justify-content: flex-end; }
.log-filters { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
@media (max-width: 980px) {
  .sms-hero { flex-direction: column; }
  .sms-hero-stats { width: 100%; }
  .broadcast-grid { grid-template-columns: 1fr; }
}
</style>
